import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { GraduationCap, Users, ArrowRight, Check, UserPlus, Search, MessageCircle, BookOpen } from "lucide-react";

export default function Landing() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 sm:pt-24 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <span className="inline-block text-xs uppercase tracking-widest text-neutral-500 font-medium mb-6">
              Aileler ve öğretmenler için
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tighter text-neutral-900">
              Çocuğunuza <em className="italic text-[color:var(--brand)]">tam</em> uyan özel öğretmeni bulun.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-neutral-600 leading-relaxed max-w-xl">
              Türkiye'nin dört bir yanındaki nitelikli, doğrulanmış özel öğretmenlerle ailenizi buluşturuyoruz. Yüz yüze ya da online — çocuğunuza uygun olanı seçin.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/giris/aile" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  data-testid="family-login-btn"
                  className="w-full sm:w-auto rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-8 h-14 text-base active:scale-95 transition-transform shadow-md shadow-blue-500/20"
                >
                  <Users size={18} className="mr-2" /> Aile Girişi
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/giris/ogretmen" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="teacher-login-btn"
                  className="w-full sm:w-auto rounded-full border-2 border-slate-900 hover:bg-slate-900 hover:text-white bg-white text-slate-900 px-8 h-14 text-base active:scale-95 transition-all"
                >
                  <GraduationCap size={18} className="mr-2" /> Öğretmen Girişi
                </Button>
              </Link>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-1.5"><Check size={14} className="text-[color:var(--mint)]" /> Doğrulanmış öğretmenler</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-[color:var(--mint)]" /> Online & Yüz yüze</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative reveal reveal-delay-2">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-neutral-100 border border-neutral-200">
              <img
                src="/hero.png"
                alt="Genç öğretmen öğrenciye birebir özel ders veriyor"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-4 shadow-lg shadow-blue-500/10 hidden sm:block" data-testid="hero-rating">
              <div className="text-xs uppercase tracking-widest text-slate-500">Ortalama</div>
              <div className="font-display text-2xl font-semibold mt-1 text-[color:var(--brand)]">4.5 / 5</div>
              <div className="text-xs text-slate-500 mt-1">Aile memnuniyeti</div>
            </div>
          </div>
        </div>
      </section>

      {/* PERSUASIVE COPY */}
      <section className="border-t border-slate-200 tint-bg">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 sm:py-32">
          <span className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">Neden özel ders?</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-slate-900">
            Bugünün çocukları neden özel derse ihtiyaç duyuyor?
          </h2>
          <div className="mt-10 space-y-6 text-base sm:text-lg text-neutral-600 leading-relaxed">
            <p>
              Dijital dünyanın dikkat dağıtan çekiciliği güçlendikçe ve müfredat yoğunlaştıkça çocuklarımızın odaklanma süresi hızla kısalıyor. Standart bir okul programı; her çocuğun kendine özgü öğrenme hızını, ilgi alanını ya da eksik kaldığı temelleri her zaman kapsayamıyor.
            </p>
            <p>
              Özel ders; çocuğunuzun birebir dinlendiği, sorularının anında yanıtlandığı ve öğrenme merakının yeniden canlandığı güvenli bir alan sunar. Bir konuya takıldığında yardım isteyebileceği; başarılı olduğunda hemen kutlanacağı bir tempo, akademik özgüveni yeniden inşa eder.
            </p>
            <p>
              Doğru öğretmen, sadece bir konuyu değil; bir öğrenme alışkanlığını, bir bakış açısını ve geleceğe olan güveni öğretir. <strong className="font-semibold text-neutral-900">Özel Ders Hocam</strong>'da, bu doğru eşleşmeyi kolaylaştırmak için ailelerin bilgilerine ihtiyaç duydukları her şeyi tek yerde topladık.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-slate-200 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">4 adımda</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              Nasıl Çalışır?
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Aileler için özel ders sürecini en sade haline indirgedik. Tek yapman gereken sırayla ilerlemek.
            </p>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 relative">
            <StepCard n="01" icon={<UserPlus size={22} />} title="Kayıt ol ve profilini kur" desc="Aile olarak birkaç dakikada üye ol, çocuğunun ihtiyaçlarını gir." />
            <StepCard n="02" icon={<Search size={22} />} title="Sana uygun öğretmeni seç" desc="Branş, sınıf ve ders türüne göre doğrulanmış öğretmenleri filtrele." />
            <StepCard n="03" icon={<MessageCircle size={22} />} title="Bizimle iletişime geç" desc="Beğendiğiniz öğretmen için iletişim sayfamızda yer alan numaradan bize ulaşın; sizi doğru şekilde yönlendirelim." />
            <StepCard n="04" icon={<BookOpen size={22} />} title="Derslere başla" desc="Online ya da yüz yüze — dilediğin formatta özel derslerin başlasın." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-neutral-200 dotted-bg">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Bugün başlayın.
          </h2>
          <p className="mt-4 text-neutral-600">Ailenize veya öğrencinize uygun tek bir tıklama uzakta.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/giris/aile"><Button size="lg" className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] h-12 px-8 text-white shadow-md shadow-blue-500/20">Aile olarak başla</Button></Link>
            <Link to="/giris/ogretmen"><Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-2 border-slate-900 hover:bg-slate-900 hover:text-white">Öğretmen olarak katıl</Button></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function StepCard({ n, icon, title, desc }) {
  return (
    <div className="relative border border-slate-200 rounded-2xl p-6 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className="absolute -top-3 left-6 bg-[color:var(--brand)] text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-widest">{n}</div>
      <div className="h-11 w-11 rounded-xl bg-[color:var(--brand)]/10 text-[color:var(--brand)] grid place-items-center">{icon}</div>
      <div className="mt-5 font-display text-lg font-semibold text-slate-900">{title}</div>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}
